"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import { useMatch } from "fansunited-data-layer/client";
import { SectionHeader, StatsHeadToHead } from "fansunited-sports-ui";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { TeamComparisonHeader } from "../shared/TeamComparisonHeader";
interface MatchStatsProps {
  homeTeamName: string;
  awayTeamName: string;
  homeTeamLogo?: string;
  awayTeamLogo?: string;
  /** Custom image node for home team (e.g., <TeamLogo>) */
  homeTeamImage?: React.ReactNode;
  /** Custom image node for away team (e.g., <TeamLogo>) */
  awayTeamImage?: React.ReactNode;
  homeTeamColor?: string;
  awayTeamColor?: string;
}
const getStyles = (leftColor?: string, rightColor?: string) => {
  // Use team colors if available, fallback to tenant primary/secondary colors via CSS variables
  const finalLeftColor = leftColor || "var(--primary)";
  const finalRightColor = rightColor || "var(--secondary)";
  return {
    padding: "md" as const,
    background: "bg-card",
    borderRadius: "lg" as const,
    itemSpacing: "md" as const,
    bar: {
      leftBarColor: "bg-[var(--stats-left-color)]",
      rightBarColor: "bg-[var(--stats-right-color)]",
      height: "md" as const,
      borderRadius: "full" as const
    },
    typography: {
      label: "text-sm font-medium text-muted-foreground whitespace-nowrap",
      leftValue: "text-sm font-bold text-foreground",
      rightValue: "text-sm font-bold text-foreground"
    },
    cssVars: {
      "--stats-left-color": finalLeftColor,
      "--stats-right-color": finalRightColor
    } as React.CSSProperties
  };
};

/**
 * MatchStats - Displays match statistics comparison for finished matches
 * Uses data from MatchProvider context
 */
export const MatchStats: React.FC<MatchStatsProps> = t0 => {
  const $ = _c(35);
  const {
    homeTeamName,
    awayTeamName,
    homeTeamLogo,
    awayTeamLogo,
    homeTeamImage,
    awayTeamImage,
    homeTeamColor,
    awayTeamColor
  } = t0;
  const {
    statistics,
    match
  } = useMatch();
  const labels = useLabels();
  if (!statistics || !match) {
    return null;
  }
  const homeStats = statistics.competitorOne;
  const awayStats = statistics.competitorTwo;
  let t1;
  if ($[0] !== labels) {
    t1 = (key, fallbackLabel) => {
      const statsLabels = labels.matchPage.statsTab as Record<string, string>;
      return statsLabels[key] || fallbackLabel;
    };
    $[0] = labels;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const getStatLabel = t1;
  let t2;
  if ($[2] !== awayStats || $[3] !== getStatLabel || $[4] !== homeStats.statistics) {
    let t3;
    if ($[6] !== awayStats || $[7] !== getStatLabel) {
      t3 = homeStat => {
        const awayStat = awayStats.statistics.find(s => s.key === homeStat.key);
        if (!awayStat) {
          return null;
        }
        const homeValue = parseFloat(homeStat.value) || 0;
        const awayValue = parseFloat(awayStat.value) || 0;
        return {
          id: homeStat.key,
          label: getStatLabel(homeStat.key, homeStat.label),
          leftValue: homeValue,
          rightValue: awayValue,
          leftDisplay: homeStat.value,
          rightDisplay: awayStat.value,
          variant: homeStat.key === "possession" ? "bar" as const : "comparison" as const
        };
      };
      $[6] = awayStats;
      $[7] = getStatLabel;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    t2 = homeStats.statistics.map(t3).filter(_temp);
    $[2] = awayStats;
    $[3] = getStatLabel;
    $[4] = homeStats.statistics;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const statItems = t2;
  if (statItems.length === 0) {
    return null;
  }
  let t3;
  if ($[9] !== awayTeamColor || $[10] !== homeTeamColor) {
    t3 = getStyles(homeTeamColor, awayTeamColor);
    $[9] = awayTeamColor;
    $[10] = homeTeamColor;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const styles = t3;
  let t4;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      typography: {
        name: "text-sm uppercase font-semibold text-foreground"
      }
    };
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== labels.matchPage.statsTab.matchStats) {
    t5 = {
      name: labels.matchPage.statsTab.matchStats,
      styles: t4
    };
    $[13] = labels.matchPage.statsTab.matchStats;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      container: "mb-2"
    };
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t5) {
    t7 = <SectionHeader entity={t5} styles={t6} />;
    $[16] = t5;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== awayTeamImage || $[19] !== awayTeamLogo || $[20] !== awayTeamName || $[21] !== homeTeamImage || $[22] !== homeTeamLogo || $[23] !== homeTeamName) {
    t8 = <TeamComparisonHeader homeTeamName={homeTeamName} awayTeamName={awayTeamName} homeTeamLogo={homeTeamLogo} awayTeamLogo={awayTeamLogo} homeTeamImage={homeTeamImage} awayTeamImage={awayTeamImage} textOverflow="break-words" />;
    $[18] = awayTeamImage;
    $[19] = awayTeamLogo;
    $[20] = awayTeamName;
    $[21] = homeTeamImage;
    $[22] = homeTeamLogo;
    $[23] = homeTeamName;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== statItems || $[26] !== styles) {
    t9 = <div className="p-3"><StatsHeadToHead items={statItems} styles={styles} /></div>;
    $[25] = statItems;
    $[26] = styles;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== styles.cssVars || $[29] !== t8 || $[30] !== t9) {
    t10 = <div className="bg-card rounded-md shadow-md overflow-hidden border border-border" style={styles.cssVars}>{t8}{t9}</div>;
    $[28] = styles.cssVars;
    $[29] = t8;
    $[30] = t9;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== t10 || $[33] !== t7) {
    t11 = <div>{t7}{t10}</div>;
    $[32] = t10;
    $[33] = t7;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  return t11;
};
function _temp(item) {
  return item !== null;
}